import { Chart, ArcElement, PieController, CategoryScale, LinearScale, Title, Tooltip, Legend } from 'chart.js';

// Register Chart.js components
Chart.register(ArcElement, PieController, CategoryScale, LinearScale, Title, Tooltip, Legend);

const CHAIN_COLOR_MAP = {
  'TA/Petro':   { bg: '#0167AA', border: '#014170' },
  "Love’s":     { bg: '#FFE002', border: '#CFC700' },
  'Maverik':    { bg: '#C9102E', border: '#891020' },
  'Pac Pride':  { bg: '#F28230', border: '#C46A23' },
  'Kwik Trip':  { bg: '#4E4F51', border: '#303133' },
  'Sapp Bros':  { bg: '#DCE3E4', border: '#B6C1C4' },
  "Casey's":    { bg: '#E8E3D7', border: '#C9C2B3' },
  'PFJ':        { bg: '#DC1730', border: '#90101E' },
  'Alsups':     { bg: '#088193', border: '#055063' }
};

// Chain Distribution Pie Chart
export default class ChainDistributionChart {
  constructor() {
    this.chart = null;
    this.savingsSummaryId = null;
    this.currentStartDate = null;
    this.currentEndDate = null;

    this.init();
  }

  init() {
    // Get savings summary ID from URL
    const pathParts = window.location.pathname.split('/');
    this.savingsSummaryId = pathParts[pathParts.length - 1];

    // Load initial data
    this.loadChainDistribution();
  }

  async loadChainDistribution() {
    if (!this.savingsSummaryId) return;

    try {
      const params = new URLSearchParams();
      if (this.currentStartDate) params.append('start_date', this.currentStartDate);
      if (this.currentEndDate) params.append('end_date', this.currentEndDate);

      const response = await fetch(`/savings_summaries/${this.savingsSummaryId}/chain_distribution?${params}`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const chainData = await response.json();
      this.renderChart(chainData);
    } catch (error) {
      console.error('Error loading chain distribution:', error);
      this.showErrorMessage('Failed to load chain distribution. Please try again.');
    }
  }

  renderChart(chainData) {
    const chartContainer = document.getElementById('current-chain-distribution-chart');
    if (!chartContainer) return;

    // Clear existing content
    chartContainer.innerHTML = '';

    if (!chainData || chainData.length === 0) {
      this.showNoDataMessage();
      return;
    }

    // Create wrapper for chart and summary with unified gray background
    const chartWrapper = document.createElement('div');
    chartWrapper.className = 'flex flex-col w-full bg-gray-50 rounded-lg';

    // Create canvas container
    const canvasContainer = document.createElement('div');
    canvasContainer.className = 'flex items-center justify-center';
    canvasContainer.style.height = '280px';

    // Create canvas element
    const canvas = document.createElement('canvas');
    canvas.id = 'chain-distribution-canvas';
    canvas.style.maxHeight = '280px';
    canvasContainer.appendChild(canvas);

    chartWrapper.appendChild(canvasContainer);
    chartContainer.appendChild(chartWrapper);

    // Prepare data for Chart.js
    const labels = chainData.map(item => item.chain_name);
    const data = chainData.map(item => item.total_gallons);
    // Generate colors for each chain
    // const colors = this.generateColors(chainData.length);
    const colors = this.generateColors(chainData.map((chain) => chain.chain_name))

    // Destroy existing chart if it exists
    if (this.chart) {
      this.chart.destroy();
    }

    // Create new chart
    this.chart = new Chart(canvas, {
      type: 'pie',
      data: {
        labels: labels,
        datasets: [{
          data: data,
          backgroundColor: colors.background,
          borderColor: colors.border,
          borderWidth: 0
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          title: {
            display: true,
            text: 'Fuel Distribution by Chain',
            font: {
              size: 16,
              weight: 'bold'
            },
            color: '#374151'
          },
          legend: {
            position: 'bottom',
            labels: {
              padding: 20,
              usePointStyle: true,
              font: {
                size: 12
              },
              color: '#6B7280'
            }
          },
          tooltip: {
            callbacks: {
              label: function(context) {
                const chainName = context.label;
                const gallons = context.parsed;
                const total = context.dataset.data.reduce((a, b) => a + b, 0);
                const percentage = ((gallons / total) * 100).toFixed(1);
                return `${chainName}: ${gallons.toLocaleString()} gal (${percentage}%)`;
              }
            }
          }
        }
      }
    });

    // Add summary information below the chart
    this.addChartSummary(chainData);
  }

  addChartSummary(chainData) {
    const chartWrapper = document.querySelector('#current-chain-distribution-chart .flex.flex-col');
    if (!chartWrapper) return;

    const totalGallons = chainData.reduce((sum, item) => sum + item.total_gallons, 0);
    const totalTransactions = chainData.reduce((sum, item) => sum + item.transaction_count, 0);

    const summaryDiv = document.createElement('div');
    summaryDiv.className = 'pt-4 border-t border-gray-300';
    summaryDiv.innerHTML = `
      <div class="space-y-3 px-4 pb-4">
        <div class="flex justify-between items-center">
          <span class="text-sm text-gray-600">Total Gallons:</span>
          <span class="text-sm font-semibold text-gray-900">${totalGallons.toLocaleString()}</span>
        </div>
        <div class="flex justify-between items-center">
          <span class="text-sm text-gray-600">Total Transactions:</span>
          <span class="text-sm font-semibold text-gray-900">${totalTransactions.toLocaleString()}</span>
        </div>
        <div class="flex justify-between items-center">
          <span class="text-sm text-gray-600">Number of Chains:</span>
          <span class="text-sm font-semibold text-gray-900">${chainData.length}</span>
        </div>
      </div>
    `;

    chartWrapper.appendChild(summaryDiv);
  }

  generateColors(chainNames) {
    console.log(chainNames)
    // chainNames is an array of chain_name values in order
    const background = [];
    const border = [];

    for (const name of chainNames) {
      if (CHAIN_COLOR_MAP[name]) {
        background.push(CHAIN_COLOR_MAP[name].bg);
        border.push(CHAIN_COLOR_MAP[name].border);
      } else {
        // Fallback, use gray if not in the map
        background.push('#6B7280');
        border.push('#4B5563');
      }
    }

    return { background, border };
  }


  showErrorMessage(message) {
    const chartContainer = document.getElementById('current-chain-distribution-chart');
    chartContainer.innerHTML = `
      <div class="h-80 flex items-center justify-center">
        <div class="text-center">
          <svg class="mx-auto h-12 w-12 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p class="mt-2 text-sm font-medium text-gray-900">Error Loading Chart</p>
          <p class="text-sm text-gray-500">${message}</p>
          <button onclick="window.chainDistributionChart.loadChainDistribution()" 
                  class="mt-2 text-xs bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700">
            Retry
          </button>
        </div>
      </div>
    `;
  }

  showNoDataMessage() {
    const chartContainer = document.getElementById('current-chain-distribution-chart');
    chartContainer.innerHTML = `
      <div class="h-80 flex items-center justify-center">
        <div class="text-center">
          <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z" />
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20.488 9H15V3.512A9.025 9.025 0 0120.488 9z" />
          </svg>
          <p class="mt-2 text-sm font-medium text-gray-900">No Chain Data</p>
          <p class="text-sm text-gray-500">No transactions found for the selected date range</p>
        </div>
      </div>
    `;
  }
}
